import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import marketingTheme from '../themes/marketingTheme';

const CyberPhysicalSystems = () => {
  const capabilityGroups = [
    [
      "Real-Time Data Integration",
      [
        "IoT sensor integration and data streaming",
        "Edge computing and real-time processing",
        "Multi-source data fusion and synchronization",
        "Industrial protocol integration (OPC-UA, MQTT, etc.)"
      ]
    ],
    [
      "Machine Perception & Vision",
      [
        "Computer vision for quality inspection",
        "Real-time object detection and tracking",
        "Anomaly detection in visual data",
        "3D sensing and spatial analysis"
      ]
    ],
    [
      "Analytics & Intelligence",
      [
        "Predictive maintenance algorithms",
        "Process optimization models",
        "Machine learning for continuous improvement",
        "Digital twin development and simulation"
      ]
    ],
    [
      "System Integration",
      [
        "Vendor-agnostic system interfaces",
        "Cloud and edge hybrid architectures",
        "Legacy system modernization",
        "Scalable deployment strategies"
      ]
    ]
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', color: 'white' }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(/images/pic08.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          py: 12,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              color: 'white',
              textAlign: 'center',
            }}
          >
            Cyber-Physical Systems
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Bridging physical operations and digital operations.
          </Typography>
        </Container>
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              Our Cyber-Physical Systems connect your physical operations and digital operations, providing real-time insights and automation. This improves efficiency, reduces downtime, and increases overall productivity.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img
                src="/images/data_mesh_hexagons.png"
                alt="Data Mesh Architecture"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              The "physical" component involves the real-time streaming of data from your operational assets, and the "cyber" component consists of analytical algorithms that extract wisdom from this data.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/1stQbXi217I?start=3&autoplay=1&loop=1&mute=1"
                title="Cyber-Physical Systems Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ maxWidth: '100%' }}
              />
            </Box>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              We design Cyber-Physical Systems tailored to the needs of your industrial organisation. We perform modelling and testing as required to ensure that a solution can be delivered in realistic increments of value.
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              Our speciality is machine perception, allowing machines to draw inferences from visual data. Our perception algorithms draw inferences from camera data to help you make informed decisions and improve your processes, reducing downtime and increasing productivity.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img
                src="/images/traversal_perception_cognition_actuation.png"
                alt="Perception Cognition Actuation"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              Our approach is vendor-agnostic. Through IIoT interconnectors, we can interface our analytical algorithms with your existing systems to provide a seamless user experience.
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              Over time, as you capture large volumes of operational data, we can transform this into wisdom, using machine learning to improve your processes.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img
                src="/images/data_driven_industrial_automation_light.png"
                alt="Data Driven Industrial Automation"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Card
                sx={{
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      color: marketingTheme.palette.primary.main,
                    }}
                  >
                    Key Benefits
                  </Typography>
                  <List>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Real-time operational insights"
                        sx={{ '& .MuiListItemText-primary': { color: 'white', fontSize: '0.9rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Reduced system downtime"
                        sx={{ '& .MuiListItemText-primary': { color: 'white', fontSize: '0.9rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Improved process efficiency"
                        sx={{ '& .MuiListItemText-primary': { color: 'white', fontSize: '0.9rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Vendor-agnostic integration"
                        sx={{ '& .MuiListItemText-primary': { color: 'white', fontSize: '0.9rem' } }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Capabilities Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 'bold',
            mb: 6,
            textAlign: 'center',
            color: marketingTheme.palette.primary.main,
          }}
        >
          Our Capabilities
        </Typography>

        <Grid container spacing={4}>
          {capabilityGroups.map((group, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                sx={{
                  p: 4,
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    color: marketingTheme.palette.primary.main,
                  }}
                >
                  {group[0]}
                </Typography>
                <List>
                  {group[1].map((capability, capIndex) => (
                    <ListItem key={capIndex} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={capability}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#2d2d33', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 'bold',
                mb: 3,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Ready to Bridge Physical and Digital?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                mb: 4,
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              Let's discuss how Cyber-Physical Systems can transform your operations.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="/contact"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#d4a028',
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CyberPhysicalSystems;
